import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ConnectImapInput,
  ListAllPulseConnectionsQuery,
  PulseActivityLogResponse,
  PulseConnectionCountsResponse,
  PulseConnectionResponse,
  PulseConnectionSettingsInput,
  PulseSignatureResponse,
} from '@orion/contracts';
import { api, getPage } from '../../../lib/api-client.js';

const KEY = ['pulse-connections'];

export function usePulseConnectionsQuery() {
  return useQuery({ queryKey: KEY, queryFn: () => api.get<PulseConnectionResponse[]>('/pulse/connections') });
}

export function useConnectImap() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: ConnectImapInput) => api.post<PulseConnectionResponse>('/pulse/connections/imap', input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY }),
  });
}

export function useUpdatePulseSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: PulseConnectionSettingsInput }) =>
      api.patch<PulseConnectionResponse>(`/pulse/connections/${id}/settings`, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY }),
  });
}

export function useDisconnectPulse() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<void>(`/pulse/connections/${id}/disconnect`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY }),
  });
}

export function useReactivatePulse() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<PulseConnectionResponse>(`/pulse/connections/${id}/reactivate`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY }),
  });
}

export function useForceSyncPulse() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<PulseConnectionResponse>(`/pulse/connections/${id}/sync`),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: KEY });
      queryClient.invalidateQueries({ queryKey: ['pulse-connection-activity', id] });
    },
  });
}

/** `start*` are full-page OAuth redirects, not API calls — same reasoning
 * `mailboxConnectUrl` in the Account ▸ Mailbox feature uses. */
export function pulseConnectUrl(provider: 'google' | 'microsoft'): string {
  return `/api/v1/pulse/connections/${provider}/start`;
}

export function usePulseActivityLogQuery(connectionId: string) {
  return useQuery({
    queryKey: ['pulse-connection-activity', connectionId],
    queryFn: () => api.get<PulseActivityLogResponse>(`/pulse/connections/${connectionId}/activity`),
    refetchInterval: 30_000,
  });
}

const ALL_KEY = ['pulse-connections-all'];
const COUNTS_KEY = ['pulse-connections-counts'];

function toQueryString(query: Partial<ListAllPulseConnectionsQuery>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== '') params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

/** The "All connections" admin view (`admin.integrations.manage`) — every
 * mailbox connection in the tenant, not just the signed-in user's own. */
export function useAllPulseConnectionsQuery(query: Partial<ListAllPulseConnectionsQuery>) {
  return useQuery({
    queryKey: [...ALL_KEY, query],
    queryFn: () => getPage<PulseConnectionResponse[]>(`/pulse/connections/all${toQueryString(query)}`),
    placeholderData: (prev) => prev,
  });
}

export function usePulseConnectionCountsQuery() {
  return useQuery({ queryKey: COUNTS_KEY, queryFn: () => api.get<PulseConnectionCountsResponse>('/pulse/connections/counts') });
}

/** Nudges a mailbox's owner to reconnect it — the admin-side counterpart to
 * that owner's own "Reconnect" action, which only they can complete. */
export function useRemindPulseOwner() {
  return useMutation({ mutationFn: (id: string) => api.post<void>(`/pulse/connections/${id}/remind`) });
}

export function useAdminDisconnectPulse() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<void>(`/pulse/connections/${id}/admin-disconnect`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ALL_KEY });
      queryClient.invalidateQueries({ queryKey: COUNTS_KEY });
    },
  });
}

export function usePulseSignatureQuery() {
  return useQuery({ queryKey: ['pulse-signature'], queryFn: () => api.get<PulseSignatureResponse>('/pulse/signature') });
}

export function useUpdatePulseSignature() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (signature: string) => api.patch<PulseSignatureResponse>('/pulse/signature', { signature }),
    onSuccess: (data) => queryClient.setQueryData(['pulse-signature'], data),
  });
}
